import {Button, Checkbox, FormControlLabel, Stack, TextField} from '@mui/material';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import React from 'react';
import PhoneInput from 'src/components/inputs/PhoneInput';
import {mapToObject} from 'src/utils/array';
import {object} from 'yup';
import ImageCropPicker from '../inputs/ImageCropPicker.js/ImageCropPicker';
import PasswordField from 'src/components/inputs/PasswordField';
import Picker from 'src/components/inputs/Picker';
import {DynamicFormConfig} from './DynamicForm.types';
import DynamicFormInput from './DynamicFormInput';

const defaultOnSubmit = (v) =>
	console.log('you forget to pass onSubmit prop, these values to be submitted : ', v);

export default function DynamicForm<Values>(props: {
	// important props
	config: DynamicFormConfig<Values>;
	initialValues: Values;
	onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => void | Promise<any>;
	// customization
	showSubmitButton: boolean;
	spacing?: number;
	submitButtonText: string;
	// for edge cases, for example when you need to add custom inputs with different layout in the end
	children?: ((props: FormikProps<Values>) => React.ReactNode) | React.ReactNode;
}) {
	const {
		config,
		spacing = 2,
		initialValues = {},
		onSubmit,
		submitButtonText = 'انشاء',
		//
		showSubmitButton = true,
		children,
	} = props ?? {};

	const getInputProps =
		({getFieldProps, ...formikProps}) =>
		(inputName) => {
			// for material ui extra props to handle the error text and visiblity
			const helperProps = {
				onWheel: (e) => e.target.blur(),
				error: Boolean(formikProps.touched[inputName] && formikProps.errors[inputName]),
				helperText: formikProps.touched[inputName] && formikProps.errors[inputName],
			};

			return {...helperProps, ...getFieldProps(inputName)};
		};

	const renderInputs = (formikProps) => {
		// const inputProps = getInputProps(formikProps);

		return (
			<Stack spacing={spacing}>
				{config.inputs.map((input) => {
					if (typeof input.component === 'function') {
						return <input.component key={input.name} formik={formikProps} />;
					} else
						return (
							<DynamicFormInput
								input={input}
								setFieldValue={formikProps.setFieldValue}
								{...getInputProps(formikProps)(input.name)}
							/>
						);
				})}

				{typeof children === 'function' &&
					children({
						...formikProps,
						getInputProps: getInputProps(formikProps),
					})}
			</Stack>
		);
	};

	const renderChild = (formikProps) => {
		return (
			<Form autoComplete="off" onSubmit={formikProps.handleSubmit}>
				{renderInputs(formikProps)}

				{showSubmitButton && (
					<Button
						disabled={formikProps.isSubmitting}
						sx={{mt: 2}}
						fullWidth
						variant="contained"
						type="submit"
					>
						{submitButtonText}
					</Button>
				)}
			</Form>
		);
	};

	// this to make the state consistant with inputs
	// so that we don't get values not exist in inputs array
	/**
	 * inital valeus : {test:'',email:'',password:''}
	 * config : {inputs:[{name:'email'}]}
	 *
	 * then it should return email only
	 * {email:''}
	 */
	const getInitalValues = () => {
		if (!config || !config.inputs || !Array.isArray(config.inputs)) return {};

		const valuesObject = mapToObject(config.inputs, (input) => ({
			[input.name]: initialValues[input.name] ?? input.value,
		}));
		return valuesObject;
	};

	const getValidationSchema = () => {
		if (!config || !config.inputs || !Array.isArray(config.inputs)) return null;

		const schemaConfigObject = mapToObject(config.inputs, (input) => ({
			[input.name]: input.validation,
		}));

		return object(schemaConfigObject);
	};

	return (
		<Formik
			enableReinitialize
			onSubmit={onSubmit ?? config.onSubmit ?? defaultOnSubmit}
			initialValues={getInitalValues()}
			validationSchema={getValidationSchema}
			// eslint-disable-next-line react/no-children-prop
			children={renderChild}
		/>
	);
}

// const configExample = {
// 	inputs: [
// 		{
// 			name: 'name',
// 			label: 'name label',
// 			placeholder: 'name placeholder',
// 			type: 'text',
// 			validation: nameValidation,
// 			value: '',
// 		},
// 		{
// 			name: 'email',
// 			label: 'email label',
// 			placeholder: 'email placeholder',
// 			type: 'email',
// 			validation: emailValidation,
// 			value: '',
// 		},
// 		{
// 			name: 'password',
// 			label: 'password label',
// 			placeholder: 'password placeholder',
// 			type: 'password',
// 			validation: passwordValidation,
// 			value: '',
// 		},
// 		{
// 			name: 'phone',
// 			label: 'phone label',
// 			placeholder: 'phone placeholder',
// 			type: 'tel',
// 			validation: phoneValidation,
// 			value: '',
// 		},
// 		{
// 			name: 'image',
// 			label: 'image label',
// 			placeholder: 'image placeholder',
// 			type: 'image-crop',
// 			value: '',
// 		},
// 	],

// 	onSubmit: (values, actions) => {
// 		createCategory(values.image);
// 	},
// };

// construct configs from state
/**
 * 	const create = (data) => {
		if (!data) return configExample;

		return {
			...configExample,
			inputs: configExample.inputs.map((input) => ({
				...input,
				value: data[input.name],
			})),
		};
	};
 */

/**
 * 
 * dyanmic select with custom component
 * 
 * {
			name: 'select',
			label: 'select label',
			placeholder: 'select placeholder',
			type: 'select',
			component: function First({formik}) {
				const categories = useCategories([]);

				return (
					<Picker
						label="الفئة"
						options={categories}
						labelKey="name"
						valueKey="id"
						onChange={(e) => formik.setFieldValue('category', e.target.value)}
					/>
				);
			},
			value: '',
		},
 */
